import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, filter, interval, map, Observable, startWith, tap } from 'rxjs';
import jwtDecode from 'jwt-decode';
import { AccessTokenResponse, AuthRequest } from '@models';
import { ApiService } from './api-service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private storageService = inject(StorageService);
  private apiService = inject(ApiService);

  private authTokenUpdate = new BehaviorSubject<boolean>(false);

  tokenChanges$ = this.authTokenUpdate.asObservable();
  tokenExpired$: Observable<boolean> = interval(1000).pipe(
    startWith(0),
    map(() => this.isTokenAlreadyExpired()),
    distinctUntilChanged(),
    filter(Boolean),
  );

  get token(): string | null {
    return this.storageService.authToken;
  }

  isTokenValid(token: string | null): boolean {
    if (!token) {
      return false;
    }
    try {
      const decodedToken = jwtDecode<Record<string, unknown>>(token);
      const currentTime = Math.floor(Date.now() / 1000);
      const exp = decodedToken['exp'];
      return typeof exp === 'number' && exp > currentTime;
    } catch (error) {
      return false;
    }
  }

  /** Exchanges the casino player id for an access token and stores it. */
  authenticate(playerId: string): Observable<AccessTokenResponse> {
    const body: AuthRequest = { playerId };
    return this.apiService
      .post<AccessTokenResponse>('auth', body)
      .pipe(tap(({ accessToken }) => this.setAuthToken(accessToken)));
  }

  setAuthToken(token: string | null): void {
    this.storageService.authToken = token;
    this.authTokenUpdate.next(true);
  }

  private isTokenAlreadyExpired(): boolean {
    const token = this.token;
    if (!token) return true;
    try {
      const decoded = jwtDecode<Record<string, unknown>>(token);
      const currentTime = Math.floor(Date.now() / 1000);
      const exp = decoded['exp'];
      return typeof exp !== 'number' || exp <= currentTime;
    } catch {
      return true;
    }
  }

  logout(): void {
    this.storageService.authToken = null;
    this.authTokenUpdate.next(false);
  }
}
